'use client';

import { ComponentPropsWithRef, HTMLAttributes } from 'react';
import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import { cn } from '../../../lib/utils';
import { Check } from '../../../icons';
import { Icon } from '../../icon/icon';

export const ComboboxItem = ({ className, children, ...props }: ComboboxPrimitive.Item.Props) => (
  <ComboboxPrimitive.Item
    data-slot="combobox-item"
    className={cn(
      // layout + sizing
      'relative flex w-full cursor-default items-center rounded-lg px-4 py-3 select-none',
      // space for the tick icon on the right
      'pr-10',
      // typography
      'typo-base text-space outline-none',
      // highlighted state (keyboard/hover)
      'data-highlighted:bg-space-04',
      // selected state
      'data-selected:bg-indigo-crush-10',
      // selected + highlighted (keyboard) — one tone darker
      'data-highlighted:data-selected:bg-indigo-crush-20',
      // disabled
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-40',
      className,
    )}
    {...props}>
    {children}
    <ComboboxPrimitive.ItemIndicator
      data-slot="combobox-item-indicator"
      render={
        <span className="pointer-events-none absolute right-4 flex size-4 items-center justify-center text-indigo-crush" />
      }>
      <Icon icon={Check} className="size-4" />
    </ComboboxPrimitive.ItemIndicator>
  </ComboboxPrimitive.Item>
);

export const ComboboxGroup = ({ className, ...props }: ComboboxPrimitive.Group.Props) => (
  <ComboboxPrimitive.Group data-slot="combobox-group" className={cn(className)} {...props} />
);

export const ComboboxGroupLabel = ({ className, ...props }: ComboboxPrimitive.GroupLabel.Props) => (
  <ComboboxPrimitive.GroupLabel
    data-slot="combobox-group-label"
    className={cn('px-4 py-2 typo-xs text-space-60', className)}
    {...props}
  />
);

export const ComboboxEmpty = ({ className, ...props }: ComboboxPrimitive.Empty.Props) => (
  <ComboboxPrimitive.Empty
    data-slot="combobox-empty"
    className={cn(
      'hidden w-full justify-center px-4 py-3 text-center typo-base text-space-60',
      'group-data-empty/combobox-content:flex',
      className,
    )}
    {...props}
  />
);

export const ComboboxSeparator = ({ className, ...props }: ComponentPropsWithRef<'div'>) => (
  <div
    data-slot="combobox-separator"
    role="separator"
    className={cn('pointer-events-none -mx-1 my-1 h-px bg-space-08', className)}
    {...props}
  />
);

export const ComboboxStatus = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <ComboboxPrimitive.Status
    data-slot="combobox-status"
    className={cn(
      'flex items-center gap-2 px-4 py-2 typo-sm text-space-60 empty:hidden',
      className,
    )}
    {...props}
  />
);
